import { useEffect, useRef, useState } from 'react'
import styles from './CanvasBox.module.scss'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { DotsThreeIcon, TextAaIcon, CopyIcon, TrashIcon, ImageIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopMenu,
    ScoopMenuItem,
    ScoopSpinner,
    ScoopText,
    ScoopCenter,
} from '../../../ui'
import { useApi } from '../../../../api/api'
import { useModal, MODALS } from '../../../modals'

export const CanvasBox = ({
    canvasID,
    canvasName,
    thumbnailURL,
    index,
    handleDeleteDialog,
    isGenerating,
    isTourWorkspace,
}) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const buttonRef = useRef()
    const [showOptions, setShowOptions] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [brokenImage, setBrokenImage] = useState(false)
    const renameModal = useModal(MODALS.CANVAS.RENAME)
    const isGuestMode = useSelector((state) => state.auth.isGuestMode)
    const apiPath = isGuestMode ? 'guest-canvasV2' : 'canvasV2'
    const { postData: postCanvasData } = useApi(
        `https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/${apiPath}`
    )
    const userID = useSelector((state) => state.auth.userID)

    const [newCanvasName, setNewCanvasName] = useState(canvasName)

    useEffect(() => {
        setNewCanvasName(canvasName)
    }, [canvasName])

    const handleClick = () => {
        dispatch({
            type: 'APPLY_LOADED_OBJECTS_STATE',
            payload: [],
        })
        dispatch({
            type: 'UPDATE_CANVAS',
            canvasID: '',
            canvasName: '',
        })
        navigate(`${canvasID}`)
    }

    const imgURL =
        `${thumbnailURL}?${new Date().getTime()}`.replace(
            'https://scoop-canvas-thumbnails.s3.us-west-2.amazonaws.com',
            'https://d2wzostg4fezcw.cloudfront.net'
        ) || 'https://static.thenounproject.com/png/5682184-200.png'

    const handleOptionsOpen = (e) => {
        e.stopPropagation()
        setMenuOpen(true)
    }

    const handleOptionsClose = (e) => {
        e?.stopPropagation()
        setMenuOpen(false)
        setShowOptions(false)
    }

    const handleDelete = (e) => {
        e.stopPropagation()
        setMenuOpen(false)
        handleDeleteDialog(canvasID)
    }

    const handleRename = async (e) => {
        e.stopPropagation()
        setMenuOpen(false)
        const renamed = await renameModal.show({
            canvasID,
            currentName: canvasName,
        })
        if (renamed) {
            setNewCanvasName(renamed)
        }
    }

    const handleDuplicate = (e) => {
        e.stopPropagation()
        setMenuOpen(false)
        const action = {
            action: 'duplicateCanvas',
            userID: userID,
            canvasID: canvasID,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        postCanvasData(action).then(() => {
            // TO-DO: maybe instead of reloading, a more elegant way is to have canvases re-read from the server and re-list canvases
            window.location.reload()
        })
    }

    return (
        <>
            <div
                className={`${styles.canvasBox} ${isGenerating ? styles.disabled : ''}`}
                onClick={handleClick}
                index={index}
                onMouseEnter={() => setShowOptions(true)}
                onMouseLeave={() => {
                    // Don't hide options if menu is open
                    if (!menuOpen) {
                        setShowOptions(false)
                    }
                }}
            >
                <div className={styles.canvasImageContainer}>
                    {isGenerating ? (
                        <div className={styles.generatingContainer}>
                            <ScoopText className={styles.generatingText}>
                                AI Canvas Generating. Please wait.
                            </ScoopText>
                            <div className={styles.generatingLoader}>
                                <ScoopSpinner size={64} />
                            </div>
                        </div>
                    ) : brokenImage ? (
                        <ScoopCenter className={styles.placeholderContainer}>
                            <ImageIcon size={160} className={styles.placeholderImage} />
                        </ScoopCenter>
                    ) : (
                        <img
                            src={imgURL}
                            alt="canvas thumbnail"
                            onError={() => setBrokenImage(true)}
                        />
                    )}
                </div>
                <div className={styles.canvasTextContainer}>
                    <ScoopText className={styles.canvasName}>
                        {newCanvasName || 'Untitled Canvas'}
                    </ScoopText>
                </div>
                {showOptions && !isTourWorkspace && (
                    <div className={styles.optionsContainer}>
                        <div />
                        <ScoopButton
                            variant="text"
                            size="xxs"
                            onClick={handleOptionsOpen}
                            className={styles.iconButton}
                            ref={buttonRef}
                        >
                            <DotsThreeIcon size={18} weight="bold" />
                        </ScoopButton>
                    </div>
                )}
                <ScoopMenu
                    open={menuOpen}
                    anchorEl={buttonRef.current}
                    onClose={handleOptionsClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <ScoopMenuItem
                        leftSection={<TextAaIcon size={18} weight="regular" />}
                        primary="Rename"
                        onClick={(e) => handleRename(e)}
                    />
                    <ScoopMenuItem
                        leftSection={<CopyIcon size={18} weight="regular" />}
                        primary="Duplicate"
                        onClick={(e) => handleDuplicate(e)}
                    />
                    <ScoopMenuItem
                        leftSection={<TrashIcon size={18} weight="regular" color="#E50B54" />}
                        primary="Delete"
                        onClick={handleDelete}
                        className={styles.deleteMenuItem}
                    />
                </ScoopMenu>
            </div>
        </>
    )
}
